import React, { useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserTie,
  faUser,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import style from './index.scss';
import { Col, Row } from 'antd';
import { toLower } from '../../../../../util/lodashlike';
import useAlertLevelConfigs from '../../../../common/hooks/useAlertLevelConfig';
import { tabKeyForAlertType } from '../tabKeyForAlertType';

export default function GraphPanel({
  alertDetails: { data },
  typlogyActions,
  alertLevels = [],
  handleGraphClick
}) {
  let details = {};

  const { getLevelColor } = useAlertLevelConfigs(alertLevels);

  // FIN-12062 — the Sender/Beneficiary/partner nodes redirect to the alert's
  // context tab, chosen by type_of_alert (transaction -> Transaction Information,
  // activity -> Activity Information). Previously every node called
  // handleGraphClick() with no arg, so it always fell to the default Activity tab.
  const contextTab = tabKeyForAlertType(data?.typeOfAlert);

  // FIN-12133: colour-code the Action by its configured severity's level colour
  // (action_severity -> alert_level_details), consistent with the Score Explanation.
  const preventionAlertColors = useCallback(
    (key) =>
      getLevelColor(
        typlogyActions?.find(
          ({ displayName }) => toLower(displayName) === toLower(key)
        )?.actionSeverity
      ),
    [typlogyActions, getLevelColor]
  );

  try {
    if (
      data?.triggeredRulesEntitySummary &&
      typeof data.triggeredRulesEntitySummary === 'string'
    ) {
      details = JSON.parse(data.triggeredRulesEntitySummary);
    } else {
      console.warn('triggeredRulesEntitySummary is missing or not a string');
    }
  } catch (error) {
    console.log('JSON parse in graph', error);
  }

  return (
    <div className={style.container}>
      <Row className={style.topSection}>
        <Col
          span={2}
          className={style.userIconWrapper}
          onClick={() => handleGraphClick(contextTab)}>
          <div>
            <FontAwesomeIcon icon={faUser} className={style.userIcon} />
            <div className={style.title}>Sender</div>
          </div>
          {Object.keys(details).map((item) => {
            const key = item.toLowerCase();
            return (
              key === 'sender' && (
                <div
                  className={style.tagWrapper}
                  key={key}
                  style={{
                    top:
                      Object.keys(details[key]).length > 1 ? '-42px' : '-11px'
                  }}>
                  {Object.keys(details[key]).map((tag) => (
                    <div
                      style={{
                        color: preventionAlertColors(tag),
                        boxShadow: `0px 0px 8px 2px ${preventionAlertColors(
                          tag
                        )}ba`
                      }}
                      className={style.tag}
                      key={`sender-${tag}`}>
                      <span>{tag}</span>
                      <span className={style.count}>{details[key][tag]}</span>
                    </div>
                  ))}
                </div>
              )
            );
          })}
        </Col>
        <Col span={18}>
          <div className={style.arrowIconGroup}>
            <FontAwesomeIcon icon={faChevronRight} />
          </div>
        </Col>
        <Col
          span={4}
          className={style.userIconWrapper}
          onClick={() => handleGraphClick(contextTab)}>
          <div>
            <FontAwesomeIcon icon={faUserTie} className={style.userIcon} />
            <div className={style.title}>Beneficiary</div>
          </div>
          {Object.keys(details).map((item) => {
            const key = item.toLowerCase();
            return (
              key === 'receiver' && (
                <div
                  className={style.tagWrapper}
                  key={key}
                  style={{
                    top:
                      Object.keys(details[key]).length > 1 ? '-42px' : '-11px',
                    right: 0
                  }}>
                  {Object.keys(details[key]).map((tag) => (
                    <div
                      style={{
                        color: preventionAlertColors(tag),
                        boxShadow: `0px 0px 8px 2px ${preventionAlertColors(
                          tag
                        )}ba`
                      }}
                      className={style.tag}
                      key={`sender-${tag}`}>
                      <span>{tag}</span>
                      <span className={style.count}>{details[key][tag]}</span>
                    </div>
                  ))}
                </div>
              )
            );
          })}
        </Col>
      </Row>
      <div className={style.bottomSection}>
        {Object.keys(details)
          .filter((key) =>
            ['sending_partner', 'receiving_partner'].includes(key.toLowerCase())
          )
          .map((partner) => (
            <div
              className={style.partnerWrapper}
              key={`partner-${partner}`}
              onClick={() => handleGraphClick(contextTab)}>
              <span className={style.dot} />
              <span
                className={style.title}
                style={{
                  paddingTop: Object.keys(details[partner])?.length === 1 && 10
                }}>
                Partner
              </span>
              <div className={style.tagWrapper}>
                {Object.keys(details[partner]).map((key) => (
                  <div className={style.tag} key={`partner-tag-${key}`}>
                    <span>{key}</span>
                    <span className={style.count}>{details[partner][key]}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
